import { createFileRoute, Link } from "@tanstack/react-router";

const SITE_URL = "https://vendingmachinefree.com";
const TITLE = "Terms & Conditions | Vending Machine Free.com";
const DESC =
  "Read the Terms & Conditions for Vending Machine Free.com — free vending machine placement, installation and service across the USA.";

const LAST_UPDATED = "June 29, 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}/terms` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Terms", item: `${SITE_URL}/terms` },
          ],
        }),
      },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/90 backdrop-blur sticky top-0 z-40">
        <div className="container-x flex h-16 md:h-20 items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold uppercase">
            Vending Machine Free.com
          </Link>
          <Link
            to="/contact"
            className="text-sm font-semibold text-steel hover:underline"
          >
            Contact
          </Link>
        </div>
      </header>

      <main className="container-x py-12 md:py-16 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl uppercase">Terms &amp; Conditions</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate dark:prose-invert mt-8 max-w-none [&_h2]:font-display [&_h2]:uppercase [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:tracking-wide [&_p]:text-foreground/90 [&_li]:text-foreground/90">
          <p>
            These Terms &amp; Conditions ("Terms") govern your use of the
            Vending Machine Free.com website (the "Site") and any vending machine placement,
            installation, restocking or maintenance services we provide ("Services"). By
            accessing the Site or submitting an application, you agree to these Terms.
          </p>

          <h2>1. About Us</h2>
          <p>
            Vending Machine Free.com ("we", "us", "our") supplies, installs and services vending
            machines to qualified businesses across the United States at no cost to the host
            location, subject to qualification and these Terms.
          </p>

          <h2>2. Free Placement Qualification</h2>
          <ul>
            <li>Free placement is offered to qualifying US-based businesses only.</li>
            <li>
              Qualification typically depends on factors such as employee headcount, daily
              visitors, operating hours, and operator access for restocking and service.
            </li>
            <li>
              We reserve the right to decline any application or to remove a machine where a
              location no longer meets minimum usage thresholds.
            </li>
          </ul>

          <h2>3. Host Location Responsibilities</h2>
          <ul>
            <li>Provide a safe, level location with a standard electrical outlet.</li>
            <li>
              Allow reasonable access for delivery, installation, restocking, cash collection
              and maintenance during agreed hours.
            </li>
            <li>Notify us promptly of damage, vandalism, theft, or service issues.</li>
            <li>
              Not relocate, modify, repair, restock or open the machine — all machines remain
              our property at all times.
            </li>
          </ul>

          <h2>4. Ownership of Equipment</h2>
          <p>
            All vending machines installed under our free placement program remain the sole
            property of Vending Machine Free.com or its operating partners. Host locations
            receive a non-exclusive right to host the equipment for the duration of the
            placement agreement.
          </p>

          <h2>5. Product Selection &amp; Pricing</h2>
          <p>
            Product mix is selected based on location type, demand and supplier availability.
            We reserve the right to adjust pricing, selection and stocked items at any time.
            Where required, we will display vend prices on the machine.
          </p>

          <h2>6. Service Response Times</h2>
          <p>
            We endeavour to be among the fastest responders in the USA. We typically reply
            to enquiries within 4–6 hours during US business hours; response times may vary
            based on team load, holidays and surge periods.
          </p>

          <h2>7. Termination &amp; Removal</h2>
          <ul>
            <li>
              Either party may terminate a placement with reasonable written notice (typically
              30 days) unless otherwise agreed.
            </li>
            <li>
              We may remove a machine immediately if it is unsafe, damaged, underperforming,
              repeatedly vandalised or located in breach of these Terms.
            </li>
          </ul>

          <h2>8. Submissions &amp; Data</h2>
          <p>
            Information you submit through our application or contact forms is used to assess
            qualification, contact you about Services and improve our offering. We do not sell
            your information. Spam and abusive submissions are rejected via CAPTCHA and
            honeypot filtering.
          </p>

          <h2>9. Acceptable Use of the Site</h2>
          <ul>
            <li>Do not attempt to disrupt, scrape at unreasonable volume, or attack the Site.</li>
            <li>Do not submit false, misleading or unlawful information.</li>
            <li>
              All content (text, logos, images) is owned by Vending Machine Free.com or its
              licensors and may not be reused without permission.
            </li>
          </ul>

          <h2>10. Disclaimers</h2>
          <p>
            The Site and Services are provided on an "as is" and "as available" basis. To the
            fullest extent permitted by law, we disclaim all warranties — express or implied —
            including merchantability, fitness for a particular purpose and non-infringement.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Vending Machine Free.com will not be liable
            for indirect, incidental, special, consequential or punitive damages arising from
            your use of the Site or the Services.
          </p>

          <h2>12. Indemnity</h2>
          <p>
            You agree to indemnify and hold Vending Machine Free.com harmless from any claims
            arising from your breach of these Terms, your misuse of the Site, or unauthorised
            use of installed equipment by you or your visitors.
          </p>

          <h2>13. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the United States and the state in which
            the host location operates, without regard to its conflict-of-law provisions.
          </p>

          <h2>14. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. The "Last updated" date above shows
            when changes took effect. Continued use of the Site or Services after an update
            constitutes acceptance of the revised Terms.
          </p>

          <h2>15. Contact</h2>
          <p>
            Questions about these Terms? Reach out through our{" "}
            <Link to="/contact" className="text-steel underline">
              contact form
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
